export const DEFAULT_DETECTION_PARAMETERS = {
  windowMinutes: 60,
  minimumDrop: 200,
  smoothingPoints: 3,
  maximumGapMinutes: 20,
  settleMinutes: 50,
  recoveryTolerance: 50,
  cooldownMinutes: 120
};

const median = values => {
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

const normalizeData = data =>
  (Array.isArray(data) ? data : [])
    .map(point => ({
      timestamp:
        point.date instanceof Date
          ? point.date.getTime()
          : new Date(point.date).getTime(),
      value: Number(point.value)
    }))
    .filter(
      point =>
        Number.isFinite(point.timestamp) && Number.isFinite(point.value)
    )
    .sort((a, b) => a.timestamp - b.timestamp);

const smoothData = (data, smoothingPoints) => {
  const width = Math.max(1, Math.floor(Number(smoothingPoints) || 1));
  if (width === 1) return data.map(point => ({ ...point }));
  const radius = Math.floor(width / 2);
  return data.map((point, index) => {
    const values = [];
    for (
      let offset = Math.max(0, index - radius);
      offset <= Math.min(data.length - 1, index + radius);
      offset++
    )
      values.push(data[offset].value);
    return { ...point, smoothedValue: median(values) };
  });
};

export function detectCollections(rawData, suppliedParameters) {
  const parameters = {
    ...DEFAULT_DETECTION_PARAMETERS,
    ...(suppliedParameters || {})
  };
  const normalized = normalizeData(rawData);
  const points = smoothData(normalized, parameters.smoothingPoints).map(
    point => ({
      ...point,
      smoothedValue:
        point.smoothedValue === undefined
          ? point.value
          : point.smoothedValue
    })
  );
  if (points.length < 2) return [];

  const windowMs = Math.max(1, parameters.windowMinutes) * 60000;
  const maximumGapMs =
    Math.max(1, parameters.maximumGapMinutes) * 60000;
  const settleMs = Math.max(0, parameters.settleMinutes) * 60000;
  const cooldownMs = Math.max(0, parameters.cooldownMinutes) * 60000;
  const minimumDrop = Math.max(0, Number(parameters.minimumDrop) || 0);
  const recoveryTolerance = Math.max(
    0,
    Number(parameters.recoveryTolerance) || 0
  );
  const candidates = [];
  const maxima = [];
  let cooldownUntil = 0;

  for (let index = 0; index < points.length; index++) {
    const point = points[index];
    if (
      index > 0 &&
      point.timestamp - points[index - 1].timestamp > maximumGapMs
    )
      maxima.length = 0;

    while (
      maxima.length > 0 &&
      point.timestamp - points[maxima[0]].timestamp > windowMs
    )
      maxima.shift();
    while (
      maxima.length > 0 &&
      points[maxima[maxima.length - 1]].smoothedValue <=
        point.smoothedValue
    )
      maxima.pop();
    maxima.push(index);

    if (point.timestamp < cooldownUntil || maxima[0] === index) continue;
    const peakIndex = maxima[0];
    const peak = points[peakIndex];
    if (peak.smoothedValue - point.smoothedValue < minimumDrop) continue;

    let troughIndex = index;
    const settleLimit = point.timestamp + settleMs;
    for (
      let scan = index + 1;
      scan < points.length && points[scan].timestamp <= settleLimit;
      scan++
    ) {
      if (
        points[scan].timestamp - points[scan - 1].timestamp >
        maximumGapMs
      )
        break;
      if (
        points[scan].smoothedValue >
        points[troughIndex].smoothedValue + recoveryTolerance
      )
        break;
      if (
        points[scan].smoothedValue <
        points[troughIndex].smoothedValue
      )
        troughIndex = scan;
    }

    const trough = points[troughIndex];
    const amount = peak.smoothedValue - trough.smoothedValue;
    if (amount >= minimumDrop) {
      candidates.push({
        key: `${peak.timestamp}-${trough.timestamp}`,
        startedAt: Math.floor(peak.timestamp / 1000),
        endedAt: Math.floor(trough.timestamp / 1000),
        amount,
        startValue: peak.smoothedValue,
        endValue: trough.smoothedValue
      });
      cooldownUntil = trough.timestamp + cooldownMs;
      index = troughIndex;
      maxima.length = 0;
    }
  }

  return candidates;
}

export function findNearestTemperature(rawData, timestampSeconds) {
  const points = normalizeData(rawData);
  if (points.length === 0) return null;
  const target = Number(timestampSeconds) * 1000;
  let nearest = null;
  points.forEach(point => {
    const distance = Math.abs(point.timestamp - target);
    if (!nearest || distance < nearest.distance)
      nearest = { distance, value: point.value };
  });
  return nearest && nearest.distance <= 1800000 ? nearest.value : null;
}



// WEBPACK FOOTER //
// ./src/utils/collectionDetection.js